// This provider is only used in client-side context

import { createContext, useContext, useEffect, useState, useCallback, useMemo, ReactNode } from 'react';
import { PaymentGatewayClient } from '../api/payment-client';
import {
  GatewayInfo,
  CheckoutOptions,
  CheckoutSession,
  SubscriptionDetails,
  PaymentMethod,
  PortalSessionOptions,
  SubscriptionCancelOptions,
  SubscriptionPauseOptions,
  CurrentSubscriptionData
} from '../types/payment';

interface PaymentContextType {
  // State
  gatewayInfo: GatewayInfo | null;
  paymentLoading: boolean;
  isPaymentConfigured: boolean;
  paymentClient: PaymentGatewayClient | null;

  // Payment methods
  createCheckout: (options: CheckoutOptions) => Promise<CheckoutSession>;
  getCheckoutUrl: (planId: string, options?: Partial<CheckoutOptions>) => Promise<string>;
  createPortalSession: (options?: PortalSessionOptions) => Promise<{ url: string }>;
  getSubscription: (userId?: string) => Promise<CurrentSubscriptionData | null>;
  cancelSubscription: (options?: SubscriptionCancelOptions) => Promise<void>;
  pauseSubscription: (options?: SubscriptionPauseOptions) => Promise<void>;
  resumeSubscription: (userId?: string) => Promise<void>;
  getPaymentMethods: (userId?: string) => Promise<PaymentMethod[]>;
  refreshGatewayInfo: () => Promise<void>;
}

const PaymentContext = createContext<PaymentContextType | null>(null);

interface PaymentProviderProps {
  children: ReactNode;
  appId: string;
  publicKey: string;
  apiHost: string;
  enabled: boolean;
  autoLoadGatewayInfo?: boolean;
  onGatewayInfoLoaded?: (info: GatewayInfo) => void;
  onPaymentError?: (error: string) => void;
  userId?: string | null | (() => string | null | Promise<string | null>);
}

export function PaymentProvider({
  children,
  appId,
  publicKey,
  apiHost,
  enabled,
  autoLoadGatewayInfo = true,
  onGatewayInfoLoaded,
  onPaymentError,
  userId
}: PaymentProviderProps) {
  const [gatewayInfo, setGatewayInfo] = useState<GatewayInfo | null>(null);
  const [paymentLoading, setPaymentLoading] = useState(false);

  // No longer using auth adapter - userId passed directly

  // Resolve userId helper
  const resolveUserId = useCallback(async (): Promise<string | null> => {
    if (typeof userId === 'function') {
      return await userId();
    }
    return userId || null;
  }, [userId]);

  // Initialize payment client
  const paymentClient = useMemo(() => {
    if (!enabled) return null;
    return new PaymentGatewayClient({
      publicKey,
      appId,
      apiHost: apiHost || 'https://usecelo.com',
      // Note: userId will be resolved dynamically in individual methods
    });
  }, [enabled, publicKey, appId, apiHost]);

  // Load gateway info
  const loadGatewayInfo = useCallback(async () => {
    if (!enabled || !paymentClient) {
      return;
    }

    setPaymentLoading(true);
    try {
      const info = await paymentClient.getGatewayInfo();
      setGatewayInfo(info);
      onGatewayInfoLoaded?.(info);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load gateway info';
      onPaymentError?.(errorMessage);
      console.error('UseCelo: Failed to load gateway info:', err);
    } finally {
      setPaymentLoading(false);
    }
  }, [enabled, paymentClient, onGatewayInfoLoaded, onPaymentError]);

  // Payment methods
  const createCheckout = useCallback(async (options: CheckoutOptions): Promise<CheckoutSession> => {
    if (!paymentClient) {
      throw new Error('Payment not enabled');
    }
    const result = await paymentClient.createCheckout(options);
    return result;
  }, [paymentClient]);

  const getCheckoutUrl = useCallback(async (planId: string, options?: Partial<CheckoutOptions>): Promise<string> => {
    const session = await createCheckout({ planId, ...options } as CheckoutOptions);
    return session.url;
  }, [createCheckout]);

  const createPortalSession = useCallback(async (options?: PortalSessionOptions): Promise<{ url: string }> => {
    if (!paymentClient) throw new Error('Payment not enabled');
    return await paymentClient.createPortalSession(options || {});
  }, [paymentClient]);

  const getSubscription = useCallback(async (userIdParam?: string): Promise<CurrentSubscriptionData | null> => {
    if (!paymentClient) throw new Error('Payment not enabled');
    const resolvedUserId = userIdParam || await resolveUserId();
    return await paymentClient.getSubscription(resolvedUserId || '');
  }, [paymentClient, resolveUserId]);

  const cancelSubscription = useCallback(async (options?: SubscriptionCancelOptions): Promise<void> => {
    if (!paymentClient) throw new Error('Payment not enabled');
    await paymentClient.cancelSubscription(options || {});
  }, [paymentClient]);

  const pauseSubscription = useCallback(async (options?: SubscriptionPauseOptions): Promise<void> => {
    if (!paymentClient) throw new Error('Payment not enabled');
    await paymentClient.pauseSubscription(options || {});
  }, [paymentClient]);

  const resumeSubscription = useCallback(async (userIdParam?: string): Promise<void> => {
    if (!paymentClient) throw new Error('Payment not enabled');
    const resolvedUserId = userIdParam || await resolveUserId();
    await paymentClient.resumeSubscription(resolvedUserId || '');
  }, [paymentClient, resolveUserId]);

  const getPaymentMethods = useCallback(async (userIdParam?: string): Promise<PaymentMethod[]> => {
    if (!paymentClient) throw new Error('Payment not enabled');
    const resolvedUserId = userIdParam || await resolveUserId();
    return await paymentClient.getPaymentMethods(resolvedUserId || '');
  }, [paymentClient, resolveUserId]);

  // Effects
  useEffect(() => {
    if (enabled && autoLoadGatewayInfo) {
      loadGatewayInfo();
    }
  }, [enabled, autoLoadGatewayInfo, loadGatewayInfo]);

  // Skip rendering if payments not enabled
  if (!enabled) {
    return <>{children}</>;
  }

  const isPaymentConfigured = gatewayInfo?.isConfigured || false;

  const contextValue: PaymentContextType = {
    gatewayInfo,
    paymentLoading,
    isPaymentConfigured,
    paymentClient,
    createCheckout,
    getCheckoutUrl,
    createPortalSession,
    getSubscription,
    cancelSubscription,
    pauseSubscription,
    resumeSubscription,
    getPaymentMethods,
    refreshGatewayInfo: loadGatewayInfo
  };

  return (
    <PaymentContext.Provider value={contextValue}>
      {children}
    </PaymentContext.Provider>
  );
}

export function usePayment() {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error('usePayment must be used within PaymentProvider');
  }
  return context;
}